import {
  ATypeCardView,
  FUNERAL_SIDO,
  FUNERAL_SIGUNGU,
  CTypeMap,
  BTypeBreadCrumb,
  GOI_WEB_URL,
  ATypeNationalMeritHead,
  FullWidthTwinButton,
  ATypeNationalMeritDescription,
  ATypeNationalMeritSummary,
  customEvent,
  useCrematoriumInquiryModal,
  getCrematoriumPhoneByCompanyname,
} from "goi_common";
import { GetStaticPaths, GetStaticProps } from "next";

import React from "react";
import { ParlorDetailProps } from "goi_common";
import { parlorRegion } from "@/constants/parlorRegion";
import { Box, Stack } from "@mui/material";
import Head from "next/head";

import crematoriums from "goi_common/src/constants/hwajang_detail.json";

const { regionColor, targetSidos } = parlorRegion;

export const getStaticPaths: GetStaticPaths = async () => {
  const paths = crematoriums
    .filter((p) => targetSidos.includes(String(p.sido)))
    .map((crema) => ({
      params: {
        companyname: crema.companyname + " 국가유공자 안내",
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  const code = params?.companyname as string;
  const companyname = code.replace(" 국가유공자 안내", "");
  let crematorium;
  let district;
  let name = "";
  let description = "";

  if (companyname) {
    crematorium = crematoriums.find(
      (crema) => crema.companyname === companyname
    );
    if (crematorium) {
      name = crematorium.companyname;

      district = crematorium.address.slice(0, 2);
      if (crematorium.address.split(" ")[0].length == 4)
        district = crematorium.address[0] + crematorium.address[2];

      description = `${crematorium.companyname} 국가유공자의 화장 비용은 얼마나 들까요? 대전충청 지역의 국가유공자 화장 준비, 상담을 통해 도와드립니다.`;
    }
  }

  return {
    props: {
      defaultCrema: crematorium,
      pageDescription: description,
      pageKeywords: `${crematorium?.companyname}, ${crematorium?.companyname} 국가유공자, ${district} 국가유공자 장례, ${district} 국가유공자 화장 비용, ${district} 국가유공자 화장장, ${district} 국가유공자 장례 혜택`,
      pageOgTitle: ` 국가유공자를 위한 화장 비용 안내 (${crematorium?.companyname}) :: 충청대전세종장례소개포털`,
      pageOgDescription: description,
      pageOgImage: crematorium?.images[0].url || "",
      hasError: !crematorium,
    },
  };
};

export default function NationalMeritCrematoriumPage({
  defaultCrema,
  pageOgTitle,
}: {
  defaultCrema: ParlorDetailProps;
  pageOgTitle: string;
}) {
  const regionCrematoriums = crematoriums.filter((p) =>
    targetSidos.includes(String(p.sido))
  );

  /** 문의 모달의 개장·이장 / 고인 확인·방문이 거는 번호. 원본에 없으면 null 이고 고이로 폴백한다. */
  const facilityPhone = getCrematoriumPhoneByCompanyname(
    defaultCrema.companyname
  );
  const { openInquiryModal } = useCrematoriumInquiryModal({
    eventDomain: "ch",
    eventPath: "nat_hr_fr_crema",
  });
  const sidoName = FUNERAL_SIDO.get(Number(defaultCrema.sido));
  const sigunguName = FUNERAL_SIGUNGU.get(Number(defaultCrema.sido))?.get(
    Number(defaultCrema.sigungu)
  );
  const carouselItems = regionCrematoriums.map((res, i) => {
    return {
      "@type": "ListItem",
      name: `(비용안내) ${res.companyname}`,
      image: `${res.images[0].url}`,
      url: `https://www.ch-funeralportal.co.kr/national-hero/free-crema/${res.companyname} 국가유공자 안내`,
      position: i + 1,
    };
  });

  const carouselRichSearch =
    regionCrematoriums.length > 6
      ? {
          "@context": "http://schema.org",
          "@type": "ItemList",
          itemListElement: carouselItems,
        }
      : {};
  return (
    <Stack>
      <Head>
        <title>{pageOgTitle}</title>
        {regionCrematoriums.length > 5 && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(carouselRichSearch),
            }}
          />
        )}
      </Head>
      <BTypeBreadCrumb
        firstItem={{
          text: `국가유공자 장례 추천`,
          href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
        }}
        secondItem={{
          text: `국가유공자 장례114`,
          href: `/national-hero/`,
          event: {
            domain: "ch",
            path: "nat_hr_crema",
            trigger_and_target: "br_crumb_nat_hr",
          },
        }}
        goiHomeEvent={{
          domain: "ch",
          path: "nat_hr_crema",
          trigger_and_target: "br_crumb_goi_home",
        }}
      />

      <Box
        style={{ backgroundColor: regionColor.c100, padding: "20px 0px 0px" }}
      >
        <ATypeNationalMeritHead
          titleSido="충청 · 대전 · 세종"
          mainColor={regionColor}
          title={`국가유공자 장례114 \n${defaultCrema.companyname} 혜택 안내`}
        />
      </Box>

      <Stack padding="24px 16px 20px" gap="12px">
        <CTypeMap defaultParlor={defaultCrema} address={defaultCrema.address} />
      </Stack>

      <Box width="100%" padding="0 20px 20px">
        <FullWidthTwinButton
          firstBtn={{
            isGoiCall: true,
            text: "상담하기",
            onClick: () => {
              openInquiryModal(facilityPhone);
              customEvent({
                domain: "ch",
                path: "nat_hr_fr_crema",
                trigger_and_target: "goi_call",
              });
            },
          }}
          secondBtn={{
            text: "비용 알아보기",
            href: `${GOI_WEB_URL}?utm_source=dogsound` + "/match/request/?utm_source=dogsound",
            onClick: () => {
              customEvent({
                domain: "ch",
                path: "nat_hr_fr_crema",
                trigger_and_target: "uv_mth_req",
              });
            },
          }}
          backgroundColor={regionColor.c300}
        />
        <ATypeNationalMeritDescription />
        <ATypeNationalMeritSummary mainColor={regionColor} />
        <Box className="subtitle_14_b" textAlign="center" marginTop="10px">
          상담을 통해 자세한 국가 유공자 장례 혜택을 안내드립니다.
        </Box>
      </Box>

      <Stack padding="24px 16px 10px" gap="12px">
        <h3 className="button_16_sb">충청 대전 세종 국가유공자 화장장</h3>
        {regionCrematoriums.map((res) => (
          <ATypeCardView
            key={res.companyname}
            name={res.companyname}
            href={`/national-hero/free-crema/${res.companyname} 국가유공자 안내/`}
            descriptions={["국가유공자 무료"]}
            imgSrc={res.images[0].url ? res.images[0].url : ""}
            event={{
              domain: "ch",
              path: "nat_hr_fr_crema",
              trigger_and_target: "uv_crema_detail",
            }}
          />
        ))}
      </Stack>
    </Stack>
  );
}
